import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Loading from "../common/Loading";
import { ACCENT } from "./shared";
import useStudioAccount from "./useStudioAccount";
import StudioGate from "./StudioGate";
import { WALLET_MESSAGES, walletApi, walletErrorMessage } from "./walletApi";
import { formatCalendarDay, formatDate, formatDateTime, formatMoney, withdrawHint } from "./walletFormat";
import { WithdrawModal, WithdrawSuccess } from "./WalletWithdraw";
import MissingItems from "./MissingItems";

// Artist Studio > Music Wallet (earnings, balance and ledger). Everything shown comes from GET /v1/artist-wallet and
// /v1/artist-wallet/transactions; amounts arrive as integer minor units and are formatted here. The Withdraw button's state,
// its hint and the tax banner follow what the server says (withdrawal.allowed, block_reason, tax.status), and the server
// checks every rule again when a withdrawal is requested.

const FIRST_PAGE = 5;
const FULL_PAGE = 25;
const TAX_FORM_PATH = "/artist-studio/wallet/tax";
const VERIFICATION_PATH = "/artist-studio/verification";
const PAYOUT_METHOD_PATH = "/artist-studio/wallet/payout-method";
const GRADIENT = "linear-gradient(90deg, #0aa5a8 0%, #22c55e 100%)";

export const TYPE_OPTIONS = [
  { value: "streaming_royalties", label: "Streaming royalties" },
  { value: "licensing_sales", label: "Licensing sales" },
  { value: "tips", label: "Tips" },
  { value: "memberships", label: "Memberships" },
  { value: "withdrawals", label: "Withdrawals" },
  { value: "refunds", label: "Refunds and reversals" },
  { value: "adjustments", label: "Adjustments" },
];

export function TaxChip({ tax }) {
  const done = tax.status === "complete";
  return (
    <span className={`inline-block rounded-full px-4 py-1.5 text-sm font-semibold ${done ? "bg-green-100 text-green-800" : "bg-amber-100 text-amber-800"}`} data-testid="tax-chip">
      {done ? "Tax: Complete" : tax.review_status === "expired" ? "Tax: Expired" : "Tax: Incomplete"}
    </span>
  );
}

function TaxBanner({ tax, onUpload }) {
  return (
    <div className="mt-5 flex flex-wrap items-center justify-between gap-3 rounded-xl bg-[#fff4ea] px-5 py-4" role="alert" data-testid="tax-banner">
      <div>
        <p className="text-[15px] font-semibold text-[#b8560f]">{tax.review_status === "expired" ? "Your tax form has expired. Send a new one to enable payouts" : "Complete your tax information to enable payouts"}</p>
        {tax.review_status === "submitted" && <p className="mt-0.5 text-sm text-[#b8560f]/80">We received your form and are reviewing it. This changes to Complete once it is verified.</p>}
        {tax.review_status === "rejected" && <p className="mt-0.5 text-sm text-[#b8560f]/80">Your last form could not be accepted. Please upload it again.</p>}
      </div>
      <button type="button" onClick={onUpload} className="rounded-full bg-[#b8560f] px-5 py-2 text-sm font-semibold text-white hover:bg-[#9d4a0d]" data-testid="tax-upload">
        {`Upload ${tax.form_label}`}
      </button>
    </div>
  );
}

function BalanceCard({ wallet, onWithdraw, onFix, opening }) {
  const cur = wallet.currency;
  const negative = wallet.available_minor < 0;
  const hint = withdrawHint(wallet);
  const allowed = wallet.withdrawal.allowed && wallet.available_minor > 0;
  const m = wallet.month;
  const reason = wallet.withdrawal.block_reason;
  return (
    <div
      className={`mt-5 rounded-2xl px-6 py-6 ${negative ? "border border-red-200 bg-white text-gray-900" : "text-white"}`}
      style={negative ? undefined : { background: GRADIENT }} data-testid="balance-card"
    >
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <p className={`text-sm ${negative ? "text-gray-500" : "text-white/90"}`}>Available for payout</p>
          <p className={`mt-1 text-[36px] font-bold leading-tight ${negative ? "text-red-600" : ""}`} data-testid="balance-amount">
            {formatMoney(wallet.available_minor, cur)} <span className={`text-base font-semibold ${negative ? "text-gray-400" : "text-white/80"}`}>{cur}</span>
          </p>
          <p className={`mt-2 text-sm ${negative ? "text-gray-600" : "text-white/95"}`} data-testid="balance-month">
            This month: {formatMoney(m.streaming_royalties_minor, cur)} streaming royalties + {formatMoney(m.licensing_sales_minor, cur)} licensing sales
            {" "}+ {formatMoney(m.tips_minor, cur)} tips + {formatMoney(m.memberships_minor, cur)} memberships
          </p>
          <p className={`mt-1 text-sm ${negative ? "text-gray-600" : "text-white/95"}`} data-testid="balance-pending">
            Pending: {formatMoney(wallet.pending_minor, cur)}
            {wallet.pending_minor > 0 && wallet.next_release_at ? ` · next release ${formatDate(wallet.next_release_at)}` : ""}
          </p>
        </div>
        <div className="flex flex-col items-end gap-2">
          <button
            type="button" onClick={onWithdraw} disabled={!allowed || opening} title={!allowed ? hint : undefined}
            aria-disabled={!allowed || opening}
            className={`rounded-full px-6 py-2.5 text-[15px] font-semibold transition ${
              allowed && !opening ? "bg-white text-gray-900 hover:bg-gray-100" : negative ? "bg-gray-100 text-gray-400 cursor-not-allowed" : "bg-white/40 text-white cursor-not-allowed"
            }`}
            data-testid="withdraw-button"
          >
            {opening ? "Checking…" : "Withdraw"}
          </button>
        </div>
      </div>
      {!allowed && hint && (
        <p className={`mt-3 text-sm ${negative ? "text-red-600" : "text-white"}`} data-testid="withdraw-hint">
          {hint}
          {reason === "KYC_INCOMPLETE" && <> <Link to={VERIFICATION_PATH} className="underline font-semibold">Complete verification</Link></>}
          {reason === "NO_PAYOUT_METHOD" && <> <button type="button" onClick={onFix} className="underline font-semibold" data-testid="hint-add-method">Add payout method</button></>}
        </p>
      )}
    </div>
  );
}

function TransactionRow({ t, currency }) {
  const credit = t.direction === "credit";
  const extras = [];
  if (t.track_title && !t.label.includes(t.track_title)) extras.push(t.track_title);
  if (t.supporter_handle && !t.label.includes(t.supporter_handle)) extras.push(`@${t.supporter_handle}`);
  return (
    <li className="flex items-start justify-between gap-4 border-b border-gray-100 py-3.5 last:border-b-0" data-testid="transaction-row">
      <div className="min-w-0">
        <p className="text-[15px] font-semibold text-gray-900 break-words">{t.label}</p>
        <p className="mt-0.5 text-[13px] text-gray-500">
          {[formatDate(t.occurred_at), ...extras, t.type_label].filter(Boolean).join(" · ")}
          {t.status === "pending" && (
            <span className="ml-2 rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-800" data-testid="transaction-pending">
              Pending until {formatDate(t.releases_at)}
            </span>
          )}
        </p>
      </div>
      <p className={`shrink-0 text-[15px] font-bold ${credit ? "text-green-700" : "text-gray-900"}`} data-testid="transaction-amount">
        {formatMoney(t.amount_minor, t.currency || currency, { signed: true })}
      </p>
    </li>
  );
}

function Transactions({ currency, refreshKey }) {
  const [q, setQ] = useState("");
  const [search, setSearch] = useState("");
  const [type, setType] = useState("");
  const [expanded, setExpanded] = useState(false);
  const [records, setRecords] = useState(null);
  const [nextCursor, setNextCursor] = useState(null);
  const [error, setError] = useState("");
  const [loadingMore, setLoadingMore] = useState(false);
  const latest = useRef(0);

  // The search runs 350 ms after the last key.
  useEffect(() => {
    const id = setTimeout(() => setSearch(q.trim()), 350);
    return () => clearTimeout(id);
  }, [q]);

  const load = useCallback(() => {
    const ticket = latest.current + 1;
    latest.current = ticket;
    setError("");
    setRecords(null);
    const params = { limit: expanded ? FULL_PAGE : FIRST_PAGE };
    if (search) params.q = search;
    if (type) params.type = type;
    walletApi.transactions(params).then(
      (r) => {
        if (ticket !== latest.current) return; // a newer search was started meanwhile
        setRecords(r?.records || []);
        setNextCursor(r?.has_more ? r.next_cursor : null);
      },
      (e) => {
        if (ticket !== latest.current) return;
        setError(walletErrorMessage(e, WALLET_MESSAGES.transactions));
        setRecords([]);
        setNextCursor(null);
      }
    );
  }, [search, type, expanded]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const loadMore = async () => {
    if (!nextCursor || loadingMore) return;
    const ticket = latest.current;
    setLoadingMore(true);
    try {
      const params = { limit: FULL_PAGE, cursor: nextCursor };
      if (search) params.q = search;
      if (type) params.type = type;
      const r = await walletApi.transactions(params);
      if (ticket !== latest.current) return;
      setRecords((prev) => [...(prev || []), ...(r?.records || [])]);
      setNextCursor(r?.has_more ? r.next_cursor : null);
    } catch (e) {
      setError(walletErrorMessage(e, WALLET_MESSAGES.transactions));
    } finally {
      setLoadingMore(false);
    }
  };

  const filtered = Boolean(search || type);
  return (
    <section className="mt-5 rounded-2xl border border-gray-200 bg-white p-5" data-testid="transactions">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-lg font-bold text-gray-900">Transactions</h2>
        <div className="flex flex-wrap items-center gap-2">
          <select
            value={type} onChange={(e) => setType(e.target.value)} aria-label="Transaction type"
            className="rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2" style={{ "--tw-ring-color": ACCENT }} data-testid="transaction-type"
          >
            <option value="">All types</option>
            {TYPE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          <input
            type="search" value={q} maxLength={100} onChange={(e) => setQ(e.target.value)} placeholder="Search transactions" aria-label="Search transactions"
            className="w-56 rounded-xl border border-gray-200 bg-white px-3.5 py-2 text-sm focus:outline-none focus:ring-2" style={{ "--tw-ring-color": ACCENT }} data-testid="transaction-search"
          />
        </div>
      </div>

      <div className="mt-2" aria-live="polite">
        {records === null ? (
          <div className="flex justify-center py-10"><Loading /></div>
        ) : error ? (
          <p className="py-8 text-center text-sm text-red-700" role="alert" data-testid="transactions-error">
            {error} <button type="button" className="underline font-semibold" onClick={load}>Try again</button>
          </p>
        ) : records.length === 0 ? (
          <p className="py-8 text-center text-gray-500" data-testid="transactions-empty">{filtered ? "No transactions match" : "No transactions yet"}</p>
        ) : (
          <ul>{records.map((t) => <TransactionRow key={t.id} t={t} currency={currency} />)}</ul>
        )}
      </div>

      {records !== null && !error && records.length > 0 && (
        <div className="mt-2 flex flex-col items-center gap-2">
          {expanded && nextCursor && (
            <button type="button" onClick={loadMore} disabled={loadingMore} className="text-sm font-semibold disabled:opacity-60" style={{ color: ACCENT }} data-testid="transactions-more">
              {loadingMore ? "Loading…" : "Load more"}
            </button>
          )}
          {!expanded && nextCursor && (
            <button type="button" onClick={() => setExpanded(true)} className="text-[15px] font-semibold" style={{ color: ACCENT }} data-testid="transactions-view-all">View all transactions</button>
          )}
          {expanded && (
            <button type="button" onClick={() => setExpanded(false)} className="text-[15px] font-semibold text-gray-600" data-testid="transactions-show-less">Show less</button>
          )}
        </div>
      )}
    </section>
  );
}

function PayoutMethod({ wallet, onEdit }) {
  const p = wallet.payout;
  return (
    <section className="mt-5 flex items-center justify-between gap-4 rounded-2xl border border-gray-200 bg-white px-5 py-4" data-testid="payout-card">
      <div className="min-w-0">
        <p className="text-[15px] font-semibold text-gray-900" data-testid="payout-label">{p.connected && p.label ? p.label : "No payout method connected"}</p>
        <p className="mt-0.5 text-sm text-gray-500" data-testid="payout-schedule">
          {p.schedule_label} · Minimum payout {formatMoney(p.minimum_minor, p.currency)}
        </p>
        {p.next_payout && (
          <p className={`mt-1 text-sm ${p.next_payout.status === "scheduled" ? "text-gray-500" : "text-amber-800"}`} data-testid="next-payout">
            Next payout {formatCalendarDay(p.next_payout.on)}
            {p.next_payout.status === "scheduled" ? ` · ${formatMoney(p.next_payout.estimated_minor, p.next_payout.currency || p.currency)} available today` : ` · ${p.next_payout.message}`}
          </p>
        )}
        {p.hold_until && (
          <p className="mt-1 text-sm text-amber-800" data-testid="payout-hold">
            Withdrawals are on hold until {formatDateTime(p.hold_until)} after your payout method change.
          </p>
        )}
      </div>
      <button type="button" onClick={onEdit} className="shrink-0 text-[15px] font-semibold text-gray-900 hover:underline" data-testid="payout-edit">
        {p.connected ? "Edit" : "Add"}
      </button>
    </section>
  );
}

function WalletBody() {
  const navigate = useNavigate();
  const [wallet, setWallet] = useState(null);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [refreshKey, setRefreshKey] = useState(0);
  const [opening, setOpening] = useState(false);
  const [modal, setModal] = useState(null); // null | "withdraw" | { success: payout }

  const loadWallet = useCallback(async () => {
    try {
      const w = await walletApi.wallet();
      setWallet(w);
      setError("");
      return w;
    } catch (e) {
      setError(walletErrorMessage(e, WALLET_MESSAGES.load));
      return null;
    }
  }, []);

  useEffect(() => { loadWallet(); }, [loadWallet]);

  // The balance and the list are read again after anything that changes them.
  const refreshAll = useCallback(async () => {
    await loadWallet();
    setRefreshKey((k) => k + 1);
  }, [loadWallet]);

  // Checks the wallet again right before the modal opens, so a button that was enabled a while ago cannot open it.
  const openWithdraw = async () => {
    if (opening) return;
    setOpening(true);
    setNotice("");
    const fresh = await loadWallet();
    setOpening(false);
    if (fresh && fresh.withdrawal.allowed && fresh.available_minor > 0) setModal("withdraw");
  };

  // "Edit", "Add" and "Change": the Payout method page (re-authentication, then Stripe's own page, happen there).
  const editMethod = () => navigate(PAYOUT_METHOD_PATH);

  if (error && !wallet) {
    return (
      <p className="text-gray-700" role="alert" data-testid="wallet-error">
        {error} <button type="button" className="underline font-semibold" onClick={loadWallet}>Try again</button>
      </p>
    );
  }
  if (!wallet) return <div className="flex justify-center py-24"><Loading /></div>;

  return (
    <div data-testid="music-wallet">
      <div className="flex flex-wrap items-center gap-4">
        <h1 className="text-[32px] font-bold leading-tight text-gray-900">Music Wallet</h1>
        <TaxChip tax={wallet.tax} />
      </div>

      {wallet.tax.status !== "complete" && <TaxBanner tax={wallet.tax} onUpload={() => navigate(TAX_FORM_PATH)} />}
      {notice && <p className="mt-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800" role="alert" data-testid="wallet-notice">{notice}</p>}
      {error && <p className="mt-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800" role="alert">{error}</p>}

      <BalanceCard wallet={wallet} onWithdraw={openWithdraw} onFix={editMethod} opening={opening} />
      <MissingItems items={wallet.withdrawal.missing_items} />
      <Transactions currency={wallet.currency} refreshKey={refreshKey} />
      <PayoutMethod wallet={wallet} onEdit={editMethod} />

      <Link to="/artist-studio/wallet/payouts" className="mt-5 inline-block text-[15px] font-semibold" style={{ color: ACCENT }} data-testid="payout-history-link">
        View payout history →
      </Link>

      <Link to="/artist-studio/wallet/monthly-statements" className="mt-5 ml-6 inline-block text-[15px] font-semibold" style={{ color: ACCENT }} data-testid="monthly-statements-link">
        Monthly statements →
      </Link>

      <Link to="/artist-studio/wallet/statements" className="mt-5 ml-6 inline-block text-[15px] font-semibold" style={{ color: ACCENT }} data-testid="royalty-statements-link">
        Royalty statements →
      </Link>

      {modal === "withdraw" && (
        <WithdrawModal
          wallet={wallet}
          onClose={() => setModal(null)}
          onChangeMethod={editMethod}
          onWalletChanged={loadWallet}
          onAccepted={(out) => { setModal({ success: out.payout }); refreshAll(); }}
        />
      )}
      {modal && modal.success && (
        <WithdrawSuccess
          wallet={wallet} payout={modal.success}
          onHistory={() => navigate("/artist-studio/wallet/payouts")}
          onDone={() => { setModal(null); refreshAll(); }}
        />
      )}
    </div>
  );
}

export default function MusicWallet() {
  const account = useStudioAccount();
  return (
    <StudioGate account={account} active="wallet" heading="" tabs={[]} maxWidth="max-w-4xl">
      <WalletBody />
    </StudioGate>
  );
}
